import { useEffect, useRef, useState } from "react";

const heroVideo =
  "https://public.readdy.ai/ai/video_res/019ed9a7-bc73-7a91-b9d4-3769cd21809a.mp4";

// How many frames to pre-extract. Higher = smoother but heavier.
// 120 gives ~30fps feel across a 3x viewport scroll.
const FRAME_COUNT = 120;

export default function Hero() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const framesRef = useRef<ImageBitmap[]>([]);
  const targetProgressRef = useRef(0);
  const currentProgressRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastDrawnIdxRef = useRef<number>(-1);

  const [ready, setReady] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);

  // ----- 1. Load video as blob, then extract N frames as ImageBitmaps -----
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    const localFrames: ImageBitmap[] = [];

    const run = async () => {
      try {
        // Load the whole video into memory.
        const res = await fetch(heroVideo);
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        createdUrl = url;

        // Offscreen video element used purely for decoding frames.
        const video = document.createElement("video");
        video.src = url;
        video.muted = true;
        video.playsInline = true;
        video.preload = "auto";
        video.crossOrigin = "anonymous";

        await new Promise<void>((resolve, reject) => {
          const onMeta = () => {
            video.removeEventListener("loadedmetadata", onMeta);
            video.removeEventListener("error", onErr);
            resolve();
          };
          const onErr = () => {
            video.removeEventListener("loadedmetadata", onMeta);
            video.removeEventListener("error", onErr);
            reject(new Error("video metadata error"));
          };
          video.addEventListener("loadedmetadata", onMeta);
          video.addEventListener("error", onErr);
        });

        if (cancelled) return;
        const duration = video.duration || 1;

        // Sequentially seek + capture each frame to an ImageBitmap.
        for (let i = 0; i < FRAME_COUNT; i++) {
          if (cancelled) return;
          const t =
            FRAME_COUNT === 1 ? 0 : (i / (FRAME_COUNT - 1)) * duration;

          await new Promise<void>((resolve) => {
            const onSeeked = () => {
              video.removeEventListener("seeked", onSeeked);
              resolve();
            };
            video.addEventListener("seeked", onSeeked);
            // Nudge slightly off exact 0 to avoid some browsers ignoring it.
            video.currentTime = Math.min(
              Math.max(t, 0.0001),
              duration - 0.0001
            );
          });

          if (cancelled) return;
          // createImageBitmap is much faster than drawImage for repeated use.
          const bmp = await createImageBitmap(video);
          if (cancelled) {
            bmp.close?.();
            return;
          }
          localFrames.push(bmp);
          setLoadProgress((i + 1) / FRAME_COUNT);
        }

        if (cancelled) return;
        framesRef.current = localFrames;
        setReady(true);
      } catch {
        // If frame extraction fails, just leave ready=false; static fallback shows.
      }
    };

    run();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      for (const f of localFrames) f.close?.();
    };
  }, []);

  // ----- 2. Size canvas to the first frame so drawing is crisp -----
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const first = framesRef.current[0];
    if (!canvas || !first) return;
    canvas.width = first.width;
    canvas.height = first.height;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.drawImage(first, 0, 0);
  }, [ready]);

  // ----- 3. Scroll-driven progress + RAF lerp + canvas draw -----
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const frames = framesRef.current;
    const lastIdx = frames.length - 1;

    const computeTarget = () => {
      const rect = wrapper.getBoundingClientRect();
      const total = wrapper.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      targetProgressRef.current = total > 0 ? scrolled / total : 0;
    };

    const tick = () => {
      const target = targetProgressRef.current;
      const cur = currentProgressRef.current;
      const diff = target - cur;
      // Smoothing factor — lower = silkier glide, higher = snappier follow.
      const next = Math.abs(diff) < 0.0005 ? target : cur + diff * 0.12;
      currentProgressRef.current = next;

      const idx = Math.min(lastIdx, Math.max(0, Math.round(next * lastIdx)));
      if (idx !== lastDrawnIdxRef.current) {
        const bmp = frames[idx];
        if (bmp) {
          ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
          lastDrawnIdxRef.current = idx;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    computeTarget();
    rafRef.current = requestAnimationFrame(tick);

    const onScroll = () => computeTarget();
    const onResize = () => computeTarget();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ready]);

  return (
    <div
      ref={wrapperRef}
      className="relative w-full"
      style={{ height: "300vh" }}
    >
      <section className="sticky top-0 w-full h-screen bg-background-50 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Subtle loader while frames bake in. Disappears once ready. */}
        {!ready && (
          <div className="absolute inset-0 z-20 flex items-end justify-start px-6 md:px-12 lg:px-20 pb-10 pointer-events-none">
            <div className="text-xs tracking-[0.2em] uppercase text-foreground-700/70">
              Preparing scene · {Math.round(loadProgress * 100)}%
            </div>
          </div>
        )}

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pt-36 md:pt-44 pb-24 h-full flex flex-col">
          <div className="max-w-2xl">
            <h1 className="font-heading text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight text-foreground-950">
              Let every plant
              <br />
              grow{" "}
              <em className="not-italic text-primary-700">
                what it was meant to be
              </em>
              .
            </h1>

            <p className="mt-7 max-w-xl text-base md:text-lg text-foreground-800 leading-relaxed">
              From a single watering can to a garden that breathes — Verdant
              tends terraces, courtyards and quiet green corners with a decade
              of horticultural craft, season after season.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}